import React, { memo, useEffect, useState, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { validators, strings, objects } from "investira.sdk";

import {
  Stack,
  CircularProgress,
  TextField,
  InputAdornment,
  Icon,
  Typography,
  Dialog,
  Divider,
  IconButton,
  Button,
  Skeleton,
  DialogContent,
  useMediaQuery,
} from "investira.react.components";

import services from "../../../services";
import utils from "../../../utils";
import { PATHS } from "../../../enums";
import withResponseHandling from "../../../hoc/withResponseHandling";
import { acPesquisasIndicadoresChanged } from "../../../store/actions";
import SearchIndicadoresAddItem from "./SearchIndicadoresAddItem";

/**
 * Renderiza o componente SearchIndicadoresAdd.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {*} props.pageIsLoading - Propriedade `pageIsLoading` do componente.
 * @param {*} props.clearOnClose - Propriedade `clearOnClose` do componente.
 * @param {*} props.responseErrorHandling - Propriedade `responseErrorHandling` do componente.
 * @param {*} props.onSearch - Propriedade `onSearch` do componente.
 * @param {*} props.onData - Propriedade `onData` do componente.
 *
 * @description
 * Este componente realiza as seguintes operacoes:
 * 1. Recebe as propriedades necessarias para compor a interface
 * 2. Organiza os estados e callbacks usados durante a renderizacao
 * 3. Renderiza a experiencia visual do organismo
 *
 * @returns {JSX.Element|null} Elemento renderizado pelo componente.
 */

const SearchIndicadoresAdd = memo((props) => {
  const PAGE_SIZE = 10;

  const storedIndicadores = useSelector((store) => store.pesquisas.indicadores);

  const navigate = useNavigate();
  const storeDispatch = useDispatch();

  const [isLoading, setRequesting] = useState(false);
  const [pesquisa, setPesquisa] = useState("");
  const [data, setData] = useState({});
  const [message, setMessage] = useState(null);
  const [open, setOpen] = useState(null);

  const inputRef = useRef();
  const placeholderRef = useRef();

  //const TOTAL_ITEMS = pages.total_items;

  const handleOpen = (pEvent) => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    placeholderRef.current.blur();
    inputRef.current.blur();
    handleClearPesquisa();
  };

  const handleChange = (pEvent) => {
    const xValue = pEvent.currentTarget.value;

    setPesquisa(pEvent.currentTarget.value);
    handleOpen(pEvent);

    if (validators.isEmpty(xValue)) {
      handleClose();
      return;
    }
  };

  const openDrawerOnFocus = (pEvent) => {
    placeholderRef.current.blur();
    handleOpen(pEvent);
  };

  const handleClearPesquisa = (pLocation = {}) => {
    setPesquisa("");
    setData({});

    if (!validators.isEmpty(pLocation?.search)) {
      const xSearch = strings.querystringToObject(pLocation.search);

      if (!validators.isEmpty(xSearch.pesquisa)) {
        delete xSearch.pesquisa;
        const xQuery = objects.objectToQueryString(xSearch);
        navigate(`/ativos${xQuery}`);
      }
    }
  };

  const handleAdd = (pEvent, pIndicador, pClearOnClose) => {
    pEvent.preventDefault();
    pEvent.stopPropagation();

    props.onClick(pIndicador);

    handleClose();
    pClearOnClose && handleClearPesquisa();
  };

  function verifySpecialChar(pStr) {
    const regex = /[./-]/;
    return regex.test(pStr);
  }

  function cleanSpecialChar(pStr) {
    const regex = /[./-]/g;
    if (verifySpecialChar(pStr)) {
      return pStr.replace(regex, "");
    }

    return pStr;
  }

  const request = useMemo(() => {
    return utils.helpers.debounce(
      (pParams, pRes, pErr, pFin) =>
        services.pesquisas.indicador(pParams, pRes, pErr, pFin),
      1000
    );
  }, []);

  function removeDoubleSpaces(pStr) {
    return pStr.replace(/\s{2,}/g, " ").trim();
  }

  function removeEmptyItems(pArr) {
    return pArr.filter((item) => item !== "");
  }

  function formatSearch(pValue) {
    let xValue = removeDoubleSpaces(pValue);
    xValue = cleanSpecialChar(pValue);

    let xStrings = xValue.split(" ");
    xStrings = removeEmptyItems(xStrings);
    xStrings = xStrings.map((xString) => {
      return `+${xString}*`.trim();
    });

    const xSearchFormated = xStrings.join("");

    return xSearchFormated;
  }

  useEffect(() => {
    if (open) {
      placeholderRef.current.blur();
      inputRef.current.focus();

      setData(storedIndicadores.resultado);
      //setPages(search.pages);
    }
  }, [open, inputRef.current, placeholderRef.current]);

  useEffect(() => {
    if (pesquisa && pesquisa.length > 1 && !props.pageIsLoading) {
      setRequesting(true);
      setData({});
      setMessage(null);

      const xPesquisa = formatSearch(pesquisa);

      request(
        {
          pesquisa: xPesquisa,
          // size: PAGE_SIZE,
          sort: "indicador",
        },
        (rRes) => {
          setData(rRes.data);
          // setPages(rRes.pages);
          storeDispatch(
            acPesquisasIndicadoresChanged({
              pesquisa: pesquisa,
              resultado: rRes.data,
            })
          );

          if (validators.isEmpty(rRes.data)) {
            setMessage("Nenhum dado encontrado");
          }
        },
        (rErr) => {
          setMessage("Ocorreu um erro durante a busca");
        },
        () => {
          setRequesting(false);
        }
      );
    }
  }, [pesquisa, request]);

  const isUpMd = useMediaQuery((theme) => theme.breakpoints.up("md"));

  const xDialogProps = isUpMd
    ? {
        fullScreen: false,
        PaperProps: {
          sx: {
            pt: 3,
            pb: 2,
            px: 2,
          },
        },
        maxWidth: "lg",
      }
    : {
        fullScreen: true,
        PaperProps: {
          sx: {
            margin: 0,
            backgroundColor: "background.dark",
          },
        },
      };

  return (
    <>
      <Stack
        id="search-area"
        sx={{
          position: "relative",
          width: "100%",
          zIndex: "999",
        }}
      >
        {props.pageIsLoading ? (
          <Skeleton variant="rounded" width="100%" height={56} />
        ) : (
          <TextField
            id="placeholder-busca-empresa"
            inputRef={placeholderRef}
            value={pesquisa}
            onFocus={openDrawerOnFocus}
            variant="outlined"
            placeholder={"Pesquise por indicadores (IPCA, IGPM...)"}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon iconName="find" size={21} color={"primary"} />
                </InputAdornment>
              ),
            }}
            disabled={props.pageIsLoading}
          />
        )}

        <Dialog
          {...xDialogProps}
          open={open}
          onClose={handleClose}
          keepMounted
          fullWidth
        >
          <DialogContent>
            <Stack direction="row" alignItems="center" spacing={2} mb={1}>
              <Stack>
                <IconButton onClick={handleClose} color={"primary"}>
                  <Icon iconName="arrow_left" size={21} />
                </IconButton>
              </Stack>
              <Stack flexGrow={1}>
                <TextField
                  id="busca-indicador"
                  inputRef={inputRef}
                  value={pesquisa}
                  onChange={handleChange}
                  sx={{ border: "none" }}
                  variant="outlined"
                  size={isUpMd ? "medium" : "small"}
                  placeholder={"Pesquise por indicadores (IPCA, IGPM...)"}
                  fullWidth
                  disabled={props.pageIsLoading}
                />
              </Stack>
            </Stack>
            <Divider />
            <Stack>
              {!isLoading && !validators.isEmpty(data) && (
                <>
                  <Stack
                    id="busca-indicador-list"
                    sx={{ px: 0 }}
                    component="ul"
                  >
                    {Object.values(data).map((xIndicador) => {
                      return (
                        <SearchIndicadoresAddItem
                          key={crypto.randomUUID()}
                          indicador={xIndicador}
                          onClick={(e) =>
                            handleAdd(e, xIndicador, props.clearOnClose)
                          }
                        />
                      );
                    })}
                  </Stack>
                  {/* <Stack
                    sx={{
                      px: 2,
                      py: 2,
                      alignItems: "center",
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={() => handleSearch(pesquisa)}
                      sx={{ maxWidth: "500px", px: 4 }}
                    >
                      Visualizar Todos ({TOTAL_ITEMS})
                    </Button>
                  </Stack> */}
                </>
              )}

              {isLoading && (
                <Stack
                  justifyContent="center"
                  alignItems="center"
                  flexGrow={1}
                  sx={{ py: 2 }}
                >
                  <CircularProgress size={24} />
                </Stack>
              )}

              {!isLoading && validators.isEmpty(data) && (
                <Stack
                  justifyContent="center"
                  alignItems="center"
                  flexGrow={1}
                  sx={{ py: 2 }}
                >
                  <Typography variant="body2" color="textPrimary">
                    {message}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </DialogContent>
        </Dialog>
      </Stack>
    </>
  );
});

export default withResponseHandling(SearchIndicadoresAdd);
